import React, { useMemo, useRef } from "react";
import { useGetProductByIdQuery } from "../../api/productApi";
import { useAppDispatch } from "../../../../../common/hooks/hooks";
import { openProductModal } from "../common/ProductModal/productModalSlice";
import { useRelatedProducts } from "../../hooks/useRelatedProducts";
import { useRecentlyViewedTracking } from "../../../../../partials/PageContent/noc/recentlyViewed/hooks/useRecentlyViewedTracking";
import Breadcrumbs from "./Breadcrumbs";
import ProductSingleInfo from "./ProductSingleInfo";
import ProductSingleTabs from "./ProductSingleTabs";
import RelatedProducts from "../common/RelatedProducts";

const ProductSingle: React.FC = () => {
  const rootRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();

  const handleOpenQuickView = (productId: number) => {
    dispatch(openProductModal({ productId }));
  };

  // Extract productId from the data attribute on the root element
  const productId = useMemo(() => {
    const rootElement = document.getElementById("product-single-root");
    const id = rootElement?.dataset.productId;
    return id ? parseInt(id, 10) : null;
  }, []);

  // Fetch main product data
  const {
    data: product,
    isLoading: isProductLoading,
    isError: isProductError,
  } = useGetProductByIdQuery(productId as number, {
    skip: !productId,
  });

  // --- Track Recently Viewed ---
  // We pass the ID only when the product data is actually loaded to ensure it's a valid view
  useRecentlyViewedTracking(product ? product.id : null);

  // --- Use custom hook to get related products data ---
  const { productIds: relatedProductIds, title: relatedProductsTitle } =
    useRelatedProducts(product, "upsell");

  return (
    <div className="product-single" ref={rootRef}>
      <div className="product-single__container">
        {/* Only render if we have a product ID to work with */}
        {productId && (
          <>
            <Breadcrumbs
              product={product}
              isProductLoading={isProductLoading}
            />
            <ProductSingleInfo
              product={product}
              isProductLoading={isProductLoading}
              isProductError={isProductError}
            />
            <ProductSingleTabs
              product={product}
              isProductLoading={isProductLoading}
              isProductError={isProductError}
            />
            <RelatedProducts
              productIds={relatedProductIds}
              title={relatedProductsTitle}
              onQuickViewClick={handleOpenQuickView}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default ProductSingle;
